import { RouterSelectors } from '@next/core-lib/router-store';

import { SessionSelectors } from '@next/core-lib/session';

import { createSelector, createFeatureSelector } from '@ngrx/store';

import { AdviceState } from '../advice/models';
import { CropAid } from '../crop-aid/crop-aid.model';
import { selectCropAids } from '../crop-aid/store/crop-aid.selectors';

import { LegalRestriction } from '../legal-restriction/legal-restriction.model';
import { selectLegalRestrictions } from '../legal-restriction/store/legal-restriction.selectors';
import { ReportSortOption } from '../reports/models';
import { TreatmentType } from '../sales-order-period/models/sales-order-period.models';
import { selectSalesOrderPeriods } from '../sales-order-period/store/sales-order-period.selectors';
import {
  AppState,
  CropFieldAdvice,
  CropAidAdvice,
  AidAdvice,
  CropFieldAdviceDetail,
  CropFieldAdviceGroup,
  ArableSessionState,
  CropAndCropAid,
  GrasslandUsage,
  WellKnownCropIds,
} from '../shared/models';
import { sortTreatments } from '../shared/util/sort-treatments';

export const selectApp = createFeatureSelector<AppState>('app');

export const selectUserSession = createFeatureSelector<{ user?: { customerId?: number } }>(
  'session',
);

export const selectSessionCustomerId = createSelector(
  selectUserSession,
  (session) => session?.user?.customerId,
);

export const selectCrops = createSelector(selectApp, (store: AppState) => store.crops || []);

export const selectNonProductiveCrops = createSelector(
  selectApp,
  (store: AppState) => store.nonProductiveCrops || [],
);

export const selectGreenManureCrops = createSelector(selectCrops, (crops) =>
  crops.filter((crop) => crop.isGreenManure),
);

export const getCropFieldIds = createSelector(
  RouterSelectors.getRouteParam('cropFieldIds'),
  (cropFieldIds: string) => (cropFieldIds ? (JSON.parse(cropFieldIds) as number[]) : []),
);

export const selectCropFieldAdvices = createSelector(
  selectApp,
  (store: AppState): CropFieldAdvice[] => store?.cropFieldAdvices || [],
);

export const selectCropFieldAdvicesByIds = (adviceIds: number[]) =>
  createSelector(selectApp, (store: AppState): CropFieldAdvice[] =>
    store?.cropFieldAdvices?.length
      ? store.cropFieldAdvices.filter((advice) => adviceIds.indexOf(advice.adviceId) !== -1)
      : [],
  );

export const selectCropFieldAdvice = (adviceId: number) =>
  createSelector(selectCropFieldAdvices, (advices: CropFieldAdvice[]) =>
    advices.find((advice) => advice.cropFieldId === adviceId),
  );

export const selectTreatments = (adviceId: number) =>
  createSelector(selectCropFieldAdvice(adviceId), (advice) => (advice ? advice.treatments : []));

export const selectGBMTreatments = (adviceId: number) =>
  createSelector(selectTreatments(adviceId), (treatments) =>
    treatments.filter((treatment) => treatment.treatmentType === TreatmentType.GBM),
  );

export const selectFertilizationTreatments = (adviceId: number) =>
  createSelector(selectTreatments(adviceId), (treatments) =>
    treatments.filter((treatment) => treatment.treatmentType === TreatmentType.Fertilization),
  );

export const selectAvailableCropFieldAdvices = (options?: { treatmentType: TreatmentType }) =>
  createSelector(
    selectCropFieldAdvices,
    getCropFieldIds,
    (cropFieldAdvices: CropFieldAdvice[], ids: number[]): CropFieldAdvice[] => {
      const availableCropFieldAdvices =
        ids.length > 0
          ? cropFieldAdvices.filter(
            (cropFieldAdvice) => ids.indexOf(cropFieldAdvice.cropFieldId) !== -1,
          )
          : cropFieldAdvices;
      return options?.treatmentType
        ? availableCropFieldAdvices.map((cropFieldAdvice) => ({
          ...cropFieldAdvice,
          treatments: cropFieldAdvice.treatments.filter(
            (treatment) => treatment.treatmentType === options.treatmentType,
          ),
        }))
        : availableCropFieldAdvices;
    },
  );

export const hasFilteredCropFieldAdvices = createSelector(
  selectCropFieldAdvices,
  selectAvailableCropFieldAdvices(),
  (cropFieldAdvices: CropFieldAdvice[], filteredCropFieldAdvices: CropFieldAdvice[]) =>
    cropFieldAdvices.length !== filteredCropFieldAdvices.length,
);

export const selectCropFieldAdvicesDetail = (_?: { treatmentType: TreatmentType }) =>
  createSelector(
    selectAvailableCropFieldAdvices(_ ? { treatmentType: _.treatmentType } : null),
    selectCropAids,
    selectSalesOrderPeriods,
    (cropFieldAdvices, cropAids, salesOrderPeriods): CropFieldAdviceDetail[] =>
      cropFieldAdvices.map((cropFieldAdvice) => ({
        ...cropFieldAdvice,
        treatments: cropFieldAdvice.treatments.map((treatment) => ({
          ...treatment,
          period: salesOrderPeriods.find((period) => period.id === treatment.periodId),
          aidAdvices: treatment.aidAdvices.map((aidAdvice) => ({
            ...aidAdvice,
            cropAid: cropAids.find((cropAid) => cropAid.id === aidAdvice.cropAidId),
          })),
        })),
      })),
  );

export const selectCropFieldsWithAdvice = (_?: { treatmentType: TreatmentType }) =>
  createSelector(
    selectAvailableCropFieldAdvices(_ ? { treatmentType: _.treatmentType } : null),
    (cropFieldAdvices: CropFieldAdvice[]) =>
      cropFieldAdvices.filter((cropFieldAdvice) => cropFieldAdvice.treatments.length > 0) || [],
  );

export const selectCropFieldAdviceIdsReport = (_?: { treatmentType: TreatmentType }) =>
  createSelector(
    RouterSelectors.getRouteParam('cropFieldAdviceIdsReport'),
    selectAvailableCropFieldAdvices(_ ? { treatmentType: _.treatmentType } : null),
    (cropFieldAdviceIdsReport, cropFieldAdvices) =>
      cropFieldAdviceIdsReport
        ? (JSON.parse(cropFieldAdviceIdsReport) as number[])
        : cropFieldAdvices.map((cropFieldAdvice) => cropFieldAdvice.cropFieldId),
  );

export const selectUniqueAdvices = ({
  treatmentType,
  splitCropFields,
}: {
  treatmentType: TreatmentType;
  splitCropFields: string;
}) =>
  createSelector(
    selectCropFieldAdvicesDetail({ treatmentType }),
    selectCropFieldAdviceIdsReport({ treatmentType }),
    selectLegalRestrictions,
    (
      cropFieldAdvices: CropFieldAdvice[],
      ids: number[],
      legalRestrictions: LegalRestriction[],
    ): CropFieldAdviceGroup[] =>
      Object.values(
        cropFieldAdvices
          .filter((cropFieldAdvice) => ids.indexOf(cropFieldAdvice.cropFieldId) !== -1)
          .map((cropFieldAdvice) => ({
            cropFieldAdvice,
            compareBy: splitCropFields
              ? cropFieldAdvice.cropFieldId
              : JSON.stringify({
                cropId: cropFieldAdvice.crop.id,
                treatments: cropFieldAdvice.treatments
                  .filter((treatment) => treatment.treatmentType === treatmentType)
                  .map(
                    ({
                      aidAdvices,
                      moment,
                      name,
                      objective,
                      periodId,
                      pointOfAttention,
                      shouldUseAreaInHectaresManual,
                    }) => ({
                      aidAdvices,
                      moment,
                      name,
                      objective,
                      periodId,
                      pointOfAttention,
                      shouldUseAreaInHectaresManual,
                    }),
                  ),
              }),
          }))
          .reduce(
            (acc, curr) => ({
              ...acc,
              [curr.compareBy]: {
                cropFieldAdvices: [
                  ...(acc[curr.compareBy] ? acc[curr.compareBy].cropFieldAdvices : []),
                  curr.cropFieldAdvice,
                ],
                cropId: curr.cropFieldAdvice.crop.id,
                cropName: curr.cropFieldAdvice.crop.name,
                cropVarietyName: curr.cropFieldAdvice?.cropVariety?.name,
                totalHectares:
                  (acc[curr.compareBy] ? acc[curr.compareBy].totalHectares : 0) +
                  curr.cropFieldAdvice.areaInHectares,
                treatments: sortTreatments(
                  curr.cropFieldAdvice.treatments.filter(
                    (treatment) => treatment.treatmentType === treatmentType,
                  ),
                ).map((treatment) => ({
                  ...treatment,
                  aidAdvices: treatment.aidAdvices.map((aidAdvice) => ({
                    ...aidAdvice,
                    legalRestriction: legalRestrictions.find(
                      (legalRestriction) =>
                        legalRestriction.cropAidId === aidAdvice.cropAidId &&
                        legalRestriction.cropId === curr.cropFieldAdvice.crop.id,
                    ),
                  })),
                })),
                compareBy: curr.compareBy,
              },
            }),
            {} as { [compareBy: string]: CropFieldAdviceGroup },
          ),
      ) as CropFieldAdviceGroup[],
  );

export const selectUniqueAdvicesWithTreatments = (filters: {
  treatmentType: TreatmentType;
  splitCropFields: string;
}) =>
  createSelector(selectUniqueAdvices(filters), (advices) =>
    advices.filter((advice) => advice.treatments.length > 0),
  );

export const selectUniqueAdvicesWithoutTreatments = (filters: {
  treatmentType: TreatmentType;
  splitCropFields: string;
}) =>
  createSelector(selectUniqueAdvices(filters), (advices) =>
    advices.filter((advice) => advice.treatments.length === 0),
  );

export const selectCropAndCropAids = (filters: {
  treatmentType: TreatmentType;
  splitCropFields: string;
}) =>
  createSelector(selectUniqueAdvicesWithTreatments(filters), (cropFieldAdviceGroups) => {
    const cropToCropAidsMap: { [key: string]: number[] } = cropFieldAdviceGroups.reduce(
      (cropAidMap, cropFieldAdviceGroup) => {
        const cropFieldAdviceGroupCropId: string = cropFieldAdviceGroup.cropId.toString();
        const cropFieldAdviceGroupCropAidIds: number[] = cropFieldAdviceGroup.treatments.reduce(
          (cropAidIds, treatment) => [
            ...cropAidIds,
            ...treatment.aidAdvices
              .map(({ cropAidId }) => cropAidId)
              .filter((cropAidId) => cropAidIds.indexOf(cropAidId) === -1),
          ],
          [] as number[],
        );

        return {
          ...cropAidMap,
          [cropFieldAdviceGroupCropId]: cropAidMap[cropFieldAdviceGroupCropId]
            ? [
              ...cropAidMap[cropFieldAdviceGroupCropId],
              ...cropFieldAdviceGroupCropAidIds.filter(
                (cropAidId) => cropAidMap[cropFieldAdviceGroupCropId].indexOf(cropAidId) === -1,
              ),
            ]
            : cropFieldAdviceGroupCropAidIds,
        };
      },
      {} as { [key: string]: number[] },
    );

    const cropAndCropAids: CropAndCropAid[] = Object.keys(cropToCropAidsMap).reduce(
      (list, cropId) => [
        ...list,
        ...cropToCropAidsMap[cropId].map((cropAidId) => ({
          cropId: parseInt(cropId, 10),
          cropAidId,
        })),
      ],
      [],
    );

    return cropAndCropAids;
  });

export const selectHectaresWithTreatments = (filters: {
  treatmentType: TreatmentType;
  splitCropFields: string;
}) =>
  createSelector(selectUniqueAdvicesWithTreatments(filters), (groups) =>
    groups.reduce((acc, curr) => acc + curr.totalHectares, 0),
  );

export const selectHectaresWithoutTreatments = (filters: {
  treatmentType: TreatmentType;
  splitCropFields: string;
}) =>
  createSelector(selectUniqueAdvicesWithoutTreatments(filters), (groups) =>
    groups.reduce((acc, curr) => acc + curr.totalHectares, 0),
  );

export const selectCropAidAdvices = (aidAdvices: AidAdvice[]) =>
  createSelector(selectCropAids, (cropAids: CropAid[]): CropAidAdvice[] =>
    aidAdvices.map((aidAdvice) => ({
      ...aidAdvice,
      ...cropAids.find((cropAid) => cropAid.id === aidAdvice.cropAidId),
    })),
  );

export const selectCropAidAdviceNamesAndDosages = (aidAdvices: AidAdvice[]) =>
  createSelector(selectCropAidAdvices(aidAdvices), (cropAidAdvices: CropAidAdvice[]): string =>
    cropAidAdvices
      .map(
        (cropAidAdvice) =>
          `${cropAidAdvice.name} ${cropAidAdvice.dosage} ${cropAidAdvice.unitOfMeasurement}`,
      )
      .join(', '),
  );

export const selectArableSession = createSelector(
  SessionSelectors.selectSessionState,
  (session: ArableSessionState) => session?.arable,
);

export const getDefaultCropYear = createSelector(selectApp, (store) => store.defaultCropYear);

export const getCropYear = createSelector(
  selectArableSession,
  getDefaultCropYear,
  (arable, defaultYear) => (arable?.cropYear ? arable.cropYear : defaultYear),
);

export const getPreviousCropYear = createSelector(getCropYear, (cropYear) => cropYear - 1);

export const getAvailableCropYears = createSelector(
  selectApp,
  (store) => store.availableCropYears || [],
);

export const selectLoggedInEmployee = createSelector(selectApp, (store) => store.loggedInEmployee);

export const selectLoggedInEmployeeAsArray = createSelector(selectApp, (store) =>
  store.loggedInEmployee ? [...[store.loggedInEmployee]] : [],
);

export const selectSkalReport = createSelector(selectApp, (store) => store.skalReport || null);

export const selectPlanetProofReport = createSelector(
  selectApp,
  (store) => store.planetProofReport || null,
);
export const selectEnvironmentalImpactReport = createSelector(
  selectApp,
  (store) => store.environmentalImpactReport,
);

export const selectEnvironmentalImpactTableReport = createSelector(
  selectEnvironmentalImpactReport,
  (report) => report?.cropFields ?? [],
);

export const getReportSort = createSelector(
  selectApp,
  (store) => store.reportSort ?? { option: ReportSortOption.sortCropFieldName, descending: false },
);

export const selectCustomerProgress = createSelector(selectApp, (store) => store.customerProgress);

export const selectCompanyGrasslandUsage = createSelector(
  createSelector(
    createFeatureSelector<AdviceState>('advice'),
    (store: AdviceState) => store.fertilizationSettings || [],
  ),
  selectCropFieldAdvices,
  selectCrops,
  (fertilizationSettings, cropFieldAdvices, crops) =>
    fertilizationSettings?.find(
      (fs) =>
        fs.adviceId ===
        cropFieldAdvices?.find(
          (cfa) =>
            cfa.crop.id ===
            crops?.find((c) => c.agrifirmCrop.id === WellKnownCropIds.GrassLandAgrifirmCropId)?.id,
        )?.adviceId,
    )?.nitrogenCalculationSettings?.grasslandUsage ?? GrasslandUsage.Graze,
);

export const selectCompanySettings = createSelector(
  selectApp,
  (store: AppState) => store.companySettings || null,
);

export const selectSoilTypes = createSelector(
  selectApp,
  (store: AppState) => store.soilTypes || null,
);
